"use client";

import React, { useState, useEffect, useRef } from 'react';
import api from '@/config/api';
import ReCAPTCHA from "react-google-recaptcha";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FiArrowRight, FiChevronDown } from "react-icons/fi";
import Image from "next/image";

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success');
  const [featuredCategories, setFeaturedCategories] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const recaptchaRef = useRef(null);

  // Get reCAPTCHA site key from environment
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  // Check if captcha is enabled (only if site key is provided)
  const isCaptchaEnabled = !!recaptchaSiteKey;

  const companyLinks = [
    { label: "About Us", href: "/about-us" },
    { label: "Categories", href: "/categories" },
    { label: "Services", href: "/servicelist" },
    { label: "Blog", href: "/blog" },
    { label: "Contact Us", href: "/contact-us" },
  ];

  // Fetch categories and services from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/getSiteData');
        const data = response.data;

        if (data) {
          // Set featured categories from sc_list, skip the first item
          if (data.sc_list && Array.isArray(data.sc_list)) {
            const categories = data.sc_list.slice(1).slice(0, 8).map(category => ({
              name: category.sc_name,
              slug: category.sc_slug,
              href: `/categories/${encodeURIComponent(category.sc_slug)}`
            }));
            setFeaturedCategories(categories);
          }

          // Set featured services from se_list, skip the first item
          if (data.se_list && Array.isArray(data.se_list)) {
            const services = data.se_list.slice(1).slice(0, 8).map(service => ({
              name: service.se_name,
              slug: service.se_slug,
              href: `/servicelist/${encodeURIComponent(service.se_slug)}/freelancers`
            }));
            setFeaturedServices(services);
          }
        }
      } catch (err) {
        console.error('Error fetching site data:', err);
      }
    };

    fetchData();
  }, []);

  const footerLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms and Conditions", href: "/terms-and-conditions" },
  ];

  // Separate function to handle the actual subscription submission
  const submitSubscription = async (token = null) => {
    setIsLoading(true);
    try {
      const requestData = { subscription_email: email };

      // Only include captcha token if captcha is enabled
      if (isCaptchaEnabled && token) {
        requestData.captcha_token = token;
      }

      const response = await api.post('/subscribe', requestData);
      const message = response?.data?.message || 'Thank you for subscribing!';
      setModalType('success');
      setModalMessage(message);
      setIsModalOpen(true);
      setEmail('');

      // Reset captcha after successful submission
      setCaptchaToken(null);
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Subscription failed. Please try again later.';
      setModalType('error');
      setModalMessage(errMsg);
      setIsModalOpen(true);

      // Reset captcha on error (but keep it visible for retry)
      setCaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      setModalType('error');
      setModalMessage('Please enter your email address');
      setIsModalOpen(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setModalType('error');
      setModalMessage('Please enter a valid email address');
      setIsModalOpen(true);
      return;
    }

    // Show captcha if it's enabled and not shown yet
    if (isCaptchaEnabled && !showCaptcha) {
      setShowCaptcha(true);
      return;
    }

    // Validate captcha only if it's enabled and shown
    if (isCaptchaEnabled && showCaptcha && !captchaToken) {
      setModalType('error');
      setModalMessage('Please complete the captcha verification');
      setIsModalOpen(true);
      return;
    }

    // If captcha is not enabled, or if it's enabled and completed, submit directly
    if (!isCaptchaEnabled || (isCaptchaEnabled && captchaToken)) {
      submitSubscription(captchaToken);
    }
  };

  // Handle captcha change
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (token && isCaptchaEnabled) {
      // If captcha is completed, automatically submit the form
      submitSubscription(token);
    }
  };

  // Handle captcha expired
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    // Show captcha again when it expires so user can retry
    setShowCaptcha(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="w-full bg-white">
      <footer className="relative rounded-[16px] sm:rounded-[24px] lg:rounded-[32px] overflow-hidden min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] mx-auto max-w-[1850px] my-2 sm:my-4 lg:my-8 pb-0 sm:pb-0 lg:pb-0">
        {/* Result Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
              <div className={`mb-3 text-center text-base font-semibold ${modalType === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {modalType === 'success' ? 'Success' : 'Error'}
              </div>
              <p className="mb-5 text-center text-sm text-gray-700">{modalMessage}</p>
              <button
                onClick={() => setIsModalOpen(false)}
                className={`w-full rounded-xl px-4 py-2 text-sm font-medium text-white ${modalType === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                OK
              </button>
            </div>
          </div>
        )}
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden rounded-[16px] sm:rounded-[24px] lg:rounded-[32px]">
          {/* Mobile: Background image */}
          <div
            className="absolute inset-0 md:hidden bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/images/footer1.jpg')"
            }}
          />
          {/* Mobile: Dark overlay for text readability */}
          <div className="absolute inset-0 md:hidden bg-black/70"></div>

          {/* Tablet and Desktop: Background image */}
          <div
            className="absolute inset-0 bg-cover bg-center hidden md:block"
            style={{
              backgroundImage: "url('/images/footer1.jpg')",
              filter: 'grayscale(0.8)'
            }}
          />
          {/* Overlay for tablet and desktop */}
          <div className="absolute inset-0 bg-black/50 lg:bg-black/70 hidden md:block"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10">
          {/* Top Section - Main Footer Content */}
          <div className="px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 pt-4 sm:pt-6 lg:pt-8">
            <div className="max-w-7xl mx-auto">
              {/* Logo */}
              <div className="flex justify-left items-left ">
                <div className="relative h-auto w-32 sm:w-36 md:w-40">
                  <Image
                    src="/images/hyrelancerWhite.png"
                    alt="Hyrelancer Logo"
                    width={160}
                    height={40}
                    className="object-contain"
                  />
                </div>
              </div>

              {/* Navigation Grid - Mobile First Approach */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8 lg:gap-12 mb-4 sm:mb-6 lg:mb-10">
                {/* Our Company */}
                <nav className="flex flex-col gap-1 sm:gap-2 lg:gap-3 items-start">
                  <h3 className="text-sm sm:text-sm md:text-base lg:text-lg font-semibold text-gray-400 mb-1 sm:mb-2">
                    Our Company
                  </h3>
                  <div className="w-full flex flex-col gap-0.5">
                    {companyLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.href}
                        className="block text-white hover:text-gray-300 transition-colors text-sm sm:text-sm md:text-md leading-relaxed"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </nav>

                {/* Featured Categories */}
                <nav className="flex flex-col gap-1 sm:gap-2 lg:gap-3 items-start">
                  <h3 className="text-sm sm:text-sm md:text-base lg:text-lg font-semibold text-gray-400 mb-1 sm:mb-2">
                    Featured Categories
                  </h3>
                  <div className="w-full flex flex-col gap-0.5">
                    {featuredCategories.length > 0 ? (
                      featuredCategories.map((category, index) => (
                        <a
                          key={index}
                          href={category.href}
                          className="block text-white hover:text-gray-300 transition-colors text-sm sm:text-sm md:text-md leading-relaxed"
                        >
                          {category.name}
                        </a>
                      ))
                    ) : (
                      <span className="text-white/60 text-sm">Loading...</span>
                    )}
                  </div>
                </nav>

                {/* Featured Services */}
                <nav className="flex flex-col gap-1 sm:gap-2 lg:gap-3 items-start">
                  <h3 className="text-sm sm:text-sm md:text-base lg:text-lg font-semibold text-gray-400 mb-1 sm:mb-2">
                    Featured Services
                  </h3>
                  <div className="w-full flex flex-col gap-0.5">
                    {featuredServices.length > 0 ? (
                      featuredServices.map((service, index) => (
                        <a
                          key={index}
                          href={service.href}
                          className="block text-white hover:text-gray-300 transition-colors text-sm sm:text-sm md:text-md leading-relaxed"
                        >
                          {service.name}
                        </a>
                      ))
                    ) : (
                      <span className="text-white/60 text-sm">Loading...</span>
                    )}
                  </div>
                </nav>

                {/* Subscribe Section - Full width on mobile, then responsive */}
                <div className="flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-7 w-full sm:col-span-2 lg:col-span-1 xl:col-span-1 items-start">
                  {/* Subscribe Input */}
                  <div className="flex flex-col gap-2 sm:gap-2 lg:gap-3 w-full">
                    <label className="text-sm sm:text-sm md:text-base lg:text-lg font-semibold text-gray-400">
                      Subscribe
                    </label>
                    <div className="flex items-center bg-[#3d5999] rounded-full px-1.5 py-1.5 w-full max-w-[280px]">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        disabled={isLoading}
                        className="flex-1 bg-white rounded-full px-3 sm:px-3 md:px-4 py-2 sm:py-2 text-gray-700 text-xs sm:text-sm md:text-md outline-none placeholder-gray-400 border-none"
                      />
                      <button
                        onClick={handleSubscribe}
                        disabled={isLoading || (isCaptchaEnabled && showCaptcha && !captchaToken)}
                        aria-label="Subscribe"
                        className={`flex items-center justify-center w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 ml-1 rounded-full ${isLoading || (isCaptchaEnabled && showCaptcha && !captchaToken)
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-[#3d5999] cursor-pointer'
                          }`}
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <FiArrowRight size={16} className="text-white sm:w-4 sm:h-4 md:w-5 md:h-5" />
                        )}
                      </button>
                    </div>

                    {/* reCAPTCHA - Only show after user enters email and clicks submit */}
                    {isCaptchaEnabled && showCaptcha && (
                      <div className="w-full max-w-[280px] mt-2">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaSiteKey}
                          onChange={handleCaptchaChange}
                          onExpired={handleCaptchaExpired}
                          theme="light"
                          size="normal"
                        />
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  <div className="flex flex-col items-start w-full gap-2 sm:gap-2 md:gap-3">
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="text-gray-400 text-xs sm:text-[10px] md:text-[12px] font-medium">Need live support?</span>
                      <a
                        href="mailto:info@hyrelancer.com"
                        className="text-white font-semibold text-sm sm:text-[12px] md:text-[14px] hover:underline"
                      >
                        info@hyrelancer.com
                      </a>
                    </div>
                  </div>

                  {/* Social Media - hidden on large screens and above */}
                  <div className="flex flex-col items-start w-full gap-2">
                    <span className="text-white text-sm sm:text-[10px] md:text-xs font-medium">Follow Us</span>
                    <div className="flex gap-2">
                      <a href="https://www.facebook.com/people/Hyrelancer/61577473019946/#" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border border-white flex items-center justify-center text-white hover:bg-white hover:text-gray-800 transition-colors">
                        <FaFacebookF size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      </a>
                      <a href="https://www.instagram.com/hyrelancer/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border border-white flex items-center justify-center text-white hover:bg-white hover:text-gray-800 transition-colors">
                        <FaInstagram size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      </a>
                      <a href="#" aria-label="Twitter" className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border border-white flex items-center justify-center text-white hover:bg-white hover:text-gray-800 transition-colors">
                        <FaXTwitter size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      </a>
                      <a href="https://www.linkedin.com/company/hyrelancerindia/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border border-white flex items-center justify-center text-white hover:bg-white hover:text-gray-800 transition-colors">
                        <FaLinkedinIn size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      </a>
                      <a href="#" aria-label="YouTube" className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border border-white flex items-center justify-center text-white hover:bg-white hover:text-gray-800 transition-colors">
                        <FaYoutube size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section - Responsive positioning */}
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-12 xl:px-12 mb-4 sm:mb-8 lg:mb-20 xl:mb-24 relative">
            {/* Divider */}
            <div className="w-full h-px bg-white/40 mb-3 sm:mb-6 lg:mb-15"></div>
            <div
              className={`
                w-full
                flex flex-col 
                items-center
                justify-between
                gap-4
                backdrop-blur-md bg-black/75 lg:bg-black/80
                rounded-2xl sm:rounded-full
                px-4 sm:px-8 md:px-10 lg:px-16 xl:px-20
                py-4 sm:py-3 md:py-4 lg:py-6
                shadow-lg
                
                /* Custom breakpoints for layout adjustments */
                2xl:flex-row
                2xl:items-center
                2xl:justify-between
              `}
            >
              {/* 
                For >1344px: All in one row
                For 1344px-1024px: Each section is in a separate row, full width, and centered/left-aligned
                For <1024px: Stack vertically, full width
              */}
              <div className={`
                flex flex-col items-center w-full
                2xl:flex-row 2xl:w-auto 2xl:items-center 2xl:gap-12
                gap-3
                [&>*]:mb-0
              `}>
                {/* Copyright */}
                <div className={`
                  flex items-center flex-shrink-0 min-w-fit
                  justify-center
                  w-full 2xl:w-auto
                  order-1
                  text-center 2xl:text-left
                `}>
                  <span className="text-white text-xs sm:text-[10px] md:text-xs lg:text-sm whitespace-nowrap">
                    Copyright © All Rights Reserved. {new Date().getFullYear()}
                  </span>
                </div>

                {/* Powered by */}
                <a
                  href="https://nexitglobalsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    flex items-center flex-shrink-0 gap-2
                    justify-center
                    w-full 2xl:w-auto
                    order-2
                    text-center 2xl:text-left
                    hover:opacity-80 transition-opacity
                  `}
                >
                  <span className="text-gray-400 text-xs sm:text-[10px] md:text-xs whitespace-nowrap">Powered by</span>
                  <Image
                    src="/images/Nexit.png"
                    alt="NEXIT"
                    width={32}
                    height={32}
                    className="h-5 w-auto sm:h-4 md:h-5 lg:h-6 rounded-full object-contain"
                  />
                </a>
              </div>

              {/* Legal Links */}
              <nav className={`
                flex flex-wrap items-center justify-center w-full
                gap-x-3 gap-y-2
                order-3
                2xl:w-auto 2xl:justify-end
                2xl:order-3
                text-center 2xl:text-right
              `}>
                {footerLinks.map((link, index) => (
                  <React.Fragment key={index}>
                    <a
                      href={link.href}
                      className="text-white hover:text-gray-300 transition-colors text-xs sm:text-[10px] md:text-xs lg:text-sm whitespace-nowrap flex items-center"
                    >
                      {link.label}
                    </a>
                    {index < footerLinks.length - 1 && (
                      <span className="text-gray-400 text-xs sm:text-[10px] md:text-xs mx-1 flex items-center">|</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Large Screen Layout - Curved Design - Only for 1400px+ screens */}

      </footer>
    </div>
  );
};

export default Footer;